import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, Outlet, useNavigate } from "react-router-dom";

import { pendingAmendment } from "@makit/shared";

import { BrandMark } from "../components/primitives";
import { markAmendmentShown, wasAmendmentShown } from "../features/policy-amendment/shown";
import { NotificationsPanel, notificationsHeading } from "../features/notifications/NotificationsPanel";
import { useNotifications } from "../features/notifications/api";
import { accountLabel } from "../lib/api";
import { useSession } from "./SessionGate";

type OpenMenu = "notif" | "account" | null;

/** 웹 공통 셸. **탭 줄이 없다.**
 *
 * 상단 탭을 여섯에서 셋으로 줄이고 나니 남은 셋 중 본문은 `내 작업` 하나였고,
 * `알림`·`마이페이지`는 서로 옮겨 다니는 화면이 아니라 **상설 메뉴**였다. 앱은 이미 그 둘을
 * 상설 메뉴로 분류해 뒀고(PRD 7.6.0), **웹에서 상설 메뉴의 자리는 헤더 우측**이다.
 * 그래서 탭 줄 자체가 없어졌다 — 정본 목업 `W-20` 이 그 판이다.
 *
 * ⚠️ PRD 7.7.0 은 아직 `상단 탭 3개`로 적혀 있다. 목업이 나중이고 PRD 7.0 이
 * "그림이 다투면 목업이 이긴다"고 정해 뒀으므로 그림을 따랐다. 문서 정정은 design-manager
 * 소관이라 여기서 하지 않는다. */
export function Shell() {
  const navigate = useNavigate();
  // ── 공개 문서 개정 고지(`W-90`)를 **이 브라우저에서 한 번** 띄운다 (PRD `7.5.17` 요건 ⑤) ──
  // 로그인한 뒤 첫 셸 화면 위에서 한 번. 띄울 개정이 없으면(상수 없음 · 시행일 자리 표시 · 이미 시행) 아무 일도
  // 없다. 띄운 표시를 먼저 적어 `확인` 없이 떠나도 다시 자동으로 뜨지 않는다. 시행일까지 다시 여는 자리는
  // 마이페이지 맨 위 줄이다(`screens/Account.tsx`). ⛔ 동의를 받는 자리가 아니다.
  useEffect(() => {
    const amendment = pendingAmendment();
    if (!amendment || wasAmendmentShown(amendment.id)) return;
    markAmendmentShown(amendment.id);
    navigate("/account/notices/privacy");
  }, [navigate]);
  return (
    <div className="wrap">
      <div className="shell">
        <AppBar />
        <div className="panel">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export function AppBar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState<OpenMenu>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const avatarRef = useRef<HTMLButtonElement>(null);
  const { session, signOut: endSession } = useSession();
  // 로그아웃은 게이트가 한다(`SessionGate` 의 `signOut`). 세션 질문 하나만 다시 물으면 작업 ·
  // 청구 내역 같은 캐시가 남고, 게이트가 지금 주소를 `from` 으로 들고 가 **다음에 로그인한
  // 셀러가 앞 셀러의 화면에서 시작했다**(2026-10-04 개인정보 버그). `W-70` 의 로그아웃 줄과
  // 같은 함수다.
  const signOut = useMutation({ mutationFn: endSession });
  // ⭐ **벨 점.** 창이 보일 때 60초마다 알림 질문 하나(`features/notifications/api.ts` 머리말의 비용).
  // 패널이 같은 키를 읽는다. 조회가 실패하면 점 없이 `알림` 이다 — `0건` 으로 적지 않는다(PRD `7.7.7`).
  const notifications = useNotifications({ poll: true });
  const unreadCount = notifications.data?.unreadCount ?? 0;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const opener = open === "notif" ? bellRef.current : avatarRef.current;
      setOpen(null);
      opener?.focus();
    }
    function onDown(e: MouseEvent) {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpen(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  function go(to: string) {
    setOpen(null);
    navigate(to);
  }

  return (
    <div className="appbar" ref={barRef}>
      <Link to="/jobs" className="wordmark">
        {/* 22px 상자라 그림도 23/40 비율을 지켜 13 으로 줄인다. */}
        <span className="mk" aria-hidden="true">
          <BrandMark size={13} />
        </span>
        makit
      </Link>

      <span className="tools">
        {/* **알림 벨** (2026-10-06 되살림, W-60). 패널은 앱 `A-30` 과 같은 함수(`notificationFeed`)로 그린다.
            ⭐ 2026-10-31: **안 읽음 점이 섰다.** 서버가 알림 행과 읽음(`readAt`)을 계정에 저장하고, 앱 `A-30`
            에도 같은 회차에 점이 섰다. 점과 라벨은 안 읽음 수에서만 나온다(PRD `7.6.12` ⓔ · `7.7.7`). */}
        <button
          type="button"
          className="iconslot"
          ref={bellRef}
          aria-haspopup="dialog"
          aria-expanded={open === "notif"}
          aria-label={notificationsHeading(unreadCount)}
          onClick={() => setOpen((v) => (v === "notif" ? null : "notif"))}
        >
          ◉
          {unreadCount > 0 ? <span className="dot" aria-hidden="true" /> : null}
        </button>
        <button
          type="button"
          className="iconslot"
          ref={avatarRef}
          aria-haspopup="menu"
          aria-expanded={open === "account"}
          aria-label="계정 메뉴"
          onClick={() => setOpen((v) => (v === "account" ? null : "account"))}
        >
          <span className="avatar-sm" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 16 16">
              <circle cx="8" cy="5.1" r="2.85" fill="currentColor" />
              <path d="M2.3 14.3c0-3.15 2.55-4.95 5.7-4.95s5.7 1.8 5.7 4.95z" fill="currentColor" />
            </svg>
          </span>
        </button>
      </span>

      {open === "notif" ? <NotificationsPanel onOpen={go} /> : null}

      {open === "account" ? (
        <span className="menupanel account" role="menu" aria-label="계정">
          <span className="mhead">
            {/* 표본 계정이 아니라 지금 로그인한 계정이다. 이름이 없을 때 `· 네이버` 처럼
                앞이 비어 나오지 않게 잇는 일은 `accountLabel` 이 한다. */}
            <span>{accountLabel(session)}</span>
          </span>
          {/* 마이페이지는 드롭다운에 담지 않는다. 결제 이력·정산 명세·사진 이용·
              내 데이터가 들어 있어 양이 많다. 아바타는 메뉴만 띄우고 본문은 전용 화면이다. */}
          <button type="button" className="mrow" role="menuitem" onClick={() => go("/account")}>
            마이페이지{" "}
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </button>
          <button
            type="button"
            className="mrow"
            role="menuitem"
            onClick={() => go("/account/settings")}
          >
            환경설정{" "}
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </button>
          {/* **살아났다 (2026-09-09).** 이 자리가 비활성이던 이유는 "인증이 없다"였고
              그 사실이 사라졌다. `W-70` 의 로그아웃 줄과 **같은 동작**이다 — 이 계정의
              세션 전부가 끊긴다. 끊긴 뒤 게이트가 이 탭의 캐시를 비우고 로그인 화면으로 보낸다.
              ⭐ **빨간색(`bad`)을 뗐다** (목업 2026-09-13 「로그아웃에서 빨간색을 뺐습니다」).
              로그아웃은 다시 로그인 한 번으로 돌아온다. 그 색의 임자는 되돌릴 수 없는 탈퇴다. */}
          <button
            type="button"
            className="mrow"
            role="menuitem"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
          >
            로그아웃
          </button>
        </span>
      ) : null}
    </div>
  );
}
